import { observer } from 'mobx-react-lite'
import { useId, useState } from 'react'
import { NotificationsView } from './NotificationsView'
import classNames from 'classnames'
import { NavLink } from 'pages/layout/NavLink'
import { NumberBubble } from 'components/NumberBubble'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { useFeatureFlags } from 'hooks/featureFlags'
import { BellIcon } from 'components/icons/Bell'
import { useNavigationCubit } from 'hooks/cubits/navigation'
import { Popover } from 'radix-ui'

export const NotificationsButton = observer(function NotificationsButton({
  className,
  containerClassName,
}: {
  className?: string
  containerClassName?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const user = useBreakoutUser()
  const { tt } = useTranslationTyped()
  const cubit = useNavigationCubit()
  const headingId = useId()

  const { showNotifications } = useFeatureFlags()

  const notificationCount = user.notifications.unreadCount

  if (!showNotifications) return null

  return (
    <Popover.Root modal open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <div
          className={classNames('relative', className)}
          // onClick is here instead of in the NavLink because we want to:
          // 1. Let radix handle the a11y logistics via `asChild`
          // 2. Prevent the default behavior of the NavLink, since we're
          //    handling it ourselves.
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <NavLink
            testId="notifications-button"
            Icon={BellIcon}
            to={null}
            label={tt.notifications.notifications()}
            className="block w-full"
          />
          {notificationCount > 0 && (
            <NumberBubble
              count={notificationCount}
              srLabel={tt.notifications.unread_notifications()}
              className={classNames('absolute', {
                'left-[-3px] top-[-3px]':
                  cubit.isClosed && notificationCount >= 10,
                'left-[1px] top-[1px]':
                  cubit.isClosed && notificationCount < 10,
                'right-3 top-1/2 -translate-y-1/2': cubit.isOpen,
              })}
            />
          )}
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="right"
          sideOffset={10}
          aria-labelledby={headingId}
          aria-modal
        >
          <NotificationsView
            className={containerClassName}
            onDismiss={() => setIsOpen(false)}
            headingId={headingId}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
})
